import * as Sentry from "@sentry/react";
import React, { useRef } from "react";
import z from "zod";

import { AiSettingsSchema } from "@performyard/models";
import { FormHandle, PYForm, VALIDATE_ON, toast } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../../components/Admin";
import PageTitle from "../../../../components/PageTitle";
import Spacer from "../../../../components/Spacer";
import { DisallowedWordsList, PreferredWritingStyleSelect } from "../components";

import { useAiSettings, useUpdateAiSettings } from "../../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import useUnsavedChanges from "../../../../hooks/useUnsavedChanges";
import { useAiSettingsDefaults } from "../hooks";

import { companyAISettingsQuery, currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

import ErrorPage from "../../../Error";
import { SectionLayout, SettingsFormFooter, SettingsToggle, UnsavedChangesTracker } from "../../Components";
import { preferredWritingStyleOptions } from "./config";

// Data loader function
export const aIRephraseAssistantSettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

const AIRephraseAssistantSettings = () => {
	const formRef = useRef<FormHandle<typeof AiSettingsSchema.shape.authoringAssistantSettings>>(null);

	// feature flags
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: reviewsEnabled } = useFeatureFlagQuery("ff-is-reviews-enabled", false);

	// AI settings data management
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	// Unsaved changes tracking
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// toggles
	const isFormAuthoringAssistantEnabled = data?.authoringAssistantEnabled ?? true;
	const isFormQuestionAssistantEnabled = data?.formQuestionAssistantEnabled ?? true;

	// toggle handlers
	const handleFormAuthoringAssistantToggle = async (val: boolean) => {
		await updateAiSettings({ authoringAssistantEnabled: val });
		toast.success(`Form Authoring Assistant ${val === true ? "Enabled" : "Disabled"}.`);
	};

	const handleFormQuestionAssistantToggle = async (val: boolean) => {
		await updateAiSettings({ formQuestionAssistantEnabled: val });
		toast.success(`Form Question Assistant ${val === true ? "Enabled" : "Disabled"}.`);
	};

	// Handle form submission and save changes to AI settings
	const handleSave = async ({
		value,
		resetForm,
	}: {
		value: z.infer<typeof AiSettingsSchema.shape.authoringAssistantSettings>;
		resetForm: () => void;
	}) => {
		try {
			await updateAiSettings({ authoringAssistantSettings: value });
			toast.success("Your changes have been saved.");

			// Reset the form normally - it will use the updated defaultValues from the cache
			resetForm();

			// Mark form as safe after successful save
			setIsSafe();
		} catch {
			toast.error("Unable to save settings.");
		}
	};

	const defaultValues = useAiSettingsDefaults({
		schema: AiSettingsSchema.shape.authoringAssistantSettings,
		data,
		extractDefaults: (allDefaults) => allDefaults.authoringAssistantSettings,
		extractData: (data) => data.authoringAssistantSettings,
	});

	if (!aiFeatureFlagEnabled || !reviewsEnabled) {
		return <ErrorPage code={404} />;
	}

	if (isLoading) {
		return null;
	}

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="AI Rephrase" />
				<AdminTitle>AI Rephrase</AdminTitle>
				<Spacer height={3} />
				<PYForm
					ref={formRef}
					id="ai-rephrase-settings-form"
					validateOn={[VALIDATE_ON.ON_SUBMIT]}
					schema={AiSettingsSchema.shape.authoringAssistantSettings}
					defaultValues={defaultValues}
					onSubmit={handleSave}
				>
					<UnsavedChangesTracker
						id="ai-rephrase-unsaved-changes"
						setIsUnsafe={setIsUnsafe}
						setIsSafe={setIsSafe}
					/>
					<SectionLayout
						key="rephrase-settings-section"
						title="AI Rephrase Assistant Settings"
						description="The AI Rephrase Assistants allow users to rephrase questions when creating review forms and to revise responses when answering text-based questions."
					>
						<SettingsToggle
							key="form-authoring-assistant-toggle"
							id="authoringAssistantEnabled"
							label="Form Authoring Assistant"
							description="Disabling this setting will hide the Form Authoring Assistant from users of the application."
							checked={isFormAuthoringAssistantEnabled}
							onCheckedChange={handleFormAuthoringAssistantToggle}
							dataTestId="form-authoring-assistant-toggle"
						/>
						<SettingsToggle
							key="form-question-assistant-toggle"
							id="formQuestionAssistantEnabled"
							label="Form Question Assistant"
							description="Disabling this setting will hide the Form Question Assistant from users of the application."
							checked={isFormQuestionAssistantEnabled}
							onCheckedChange={handleFormQuestionAssistantToggle}
							dataTestId="form-question-assistant-toggle"
						/>
						<PreferredWritingStyleSelect
							id="preferredWritingStyle"
							dataTestId="select-preferred-writing-style"
							label="Preferred Writing Style"
							description="Select a preferred writing style for the AI Rephrase Assistants."
							options={preferredWritingStyleOptions}
						/>
						<DisallowedWordsList
							id="disallowedWords"
							label="Disallowed Word List"
							description="Prevent specific words from being included by the AI Rephrase Assistants. Profanity is disallowed automatically."
						/>
					</SectionLayout>
					<SettingsFormFooter
						key="rephrase-settings-footer"
						formId="ai-rephrase-settings-form"
						isPending={isPending}
					/>
				</PYForm>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AIRephraseAssistantSettings;
